import { useEffect, useState } from 'react'
import { api, type DraftPreparation, type DraftQuotation } from '../lib/api.ts'
import { Button } from '../components/Button.tsx'
import { Input } from '../components/Input.tsx'
import { ProductPicker } from '../components/ProductPicker.tsx'
import { RuleScenario } from '../components/RuleScenario.tsx'
import { PreparationEditor } from '../components/PreparationEditor.tsx'
import { ProgressTrack } from '../components/ProgressTrack.tsx'
import { icons } from '../components/icons.ts'
import { forgetDraft, loadDraft, saveDraft } from '../lib/draftStore.ts'
import { Preparing } from '../components/Preparing.tsx'

/**
 * Editing one simulated quotation, on its own page.
 *
 * Split out of the simulation index because the two do different jobs. That
 * page is about starting and finding runs — upload, the demo catalogue, what
 * has been dispatched before. This one is about building a specific
 * disagreement, and everything that is not the prescription and the form is
 * in the way of that.
 *
 * The upload id is the address. It is the id the server already assigned, so
 * the page can be reloaded, bookmarked, or come back to tomorrow, and the
 * image and the reading are both fetchable from it.
 */

export interface SimulationProps {
  readonly uploadId: string
  readonly onOpenVerification: (id: string) => void
  readonly onBack: () => void
  /** Back to the golden-set case this was run from, when it was run from one. */
  readonly onOpenCase?: ((caseId: string) => void) | undefined
}

export function Simulation({ uploadId, onOpenVerification, onBack, onOpenCase }: SimulationProps) {
  const [status, setStatus] = useState<string>('preparing')
  const [quotation, setQuotation] = useState<DraftQuotation | undefined>()
  const [imageUrl, setImageUrl] = useState<string | undefined>()
  const [goldenCaseId, setGoldenCaseId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [step, setStep] = useState(0)
  const [visited, setVisited] = useState<ReadonlySet<number>>(new Set([0]))
  /*
   * Whether the locally-stored draft has been consulted yet.
   *
   * It has to happen before the first save, or the effect below writes the
   * server's untouched reading over the edits somebody came back for.
   */
  const [restored, setRestored] = useState(false)

  useEffect(() => {
    const draft = loadDraft(uploadId)
    if (draft !== undefined) {
      setQuotation(draft.quotation)
      setStep(draft.step)
      setVisited(new Set([draft.step]))
    }
    setRestored(true)
  }, [uploadId])

  useEffect(() => {
    // Fetched rather than linked: the image route is behind the session
    // middleware and an <img> cannot carry an Authorization header.
    let live = true
    let created: string | undefined
    void api.simulationImageBlob(uploadId).then((url) => {
      created = url
      if (live) setImageUrl(url)
      else if (url !== undefined) URL.revokeObjectURL(url)
    })
    return () => {
      live = false
      if (created !== undefined) URL.revokeObjectURL(created)
    }
  }, [uploadId])

  useEffect(() => {
    // Following the reading. It stops the moment there is a quotation to edit
    // or a reason it will never arrive.
    if (!restored || quotation !== undefined || status === 'failed') return undefined

    const tick = () => {
      void api
        .simulationUpload(uploadId)
        .then((row) => {
          setStatus(row.status)
          if (row.failure_reason !== null) setError(row.failure_reason)
          if (row.derived_quotation !== null) setQuotation(row.derived_quotation)
          setGoldenCaseId(row.golden_case_id)
        })
        .catch(() => undefined)
    }
    tick()
    const timer = setInterval(tick, 2000)
    return () => clearInterval(timer)
  }, [uploadId, quotation, status, restored])

  useEffect(() => {
    if (!restored || quotation === undefined) return
    saveDraft({ uploadId, quotation, step })
  }, [uploadId, quotation, step, restored])

  function goToStep(next: number) {
    const clamped = Math.max(0, next)
    setVisited((seen) => new Set([...seen, step, clamped]))
    setStep(clamped)
  }

  function editDocument(
    patch: Partial<
      Pick<DraftQuotation, 'patientName' | 'prescriberName' | 'prescriberRegistration'>
    >,
  ) {
    setQuotation((current) => (current === undefined ? current : { ...current, ...patch }))
  }

  /** Rewrites one preparation in place, leaving the others untouched. */
  function mapPreparation(f: number, change: (preparation: DraftPreparation) => DraftPreparation) {
    setQuotation((current) =>
      current === undefined
        ? current
        : {
            ...current,
            preparations: current.preparations.map((preparation, i) =>
              i === f ? change(preparation) : preparation,
            ),
          },
    )
  }

  async function dispatch() {
    if (quotation === undefined) return
    setBusy(true)
    try {
      const { verificationId } = await api.dispatchSimulation(uploadId, quotation)
      // The draft became an verification. Keeping it would offer to resume something
      // that has already been dispatched.
      forgetDraft(uploadId)
      onOpenVerification(verificationId)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao disparar')
      setBusy(false)
    }
  }

  const back = (
    <>
      <Button
        variant="tertiary"
        tone="neutral"
        size="small"
        leadingIcon={icons.back}
        onClick={onBack}
      >
        Simulações
      </Button>
      {/*
        Where this came from, when it came from somewhere.
        
        A simulation run from a golden-set case is being compared against an
        answer, and the answer is one click away. Without this the two screens
        are related only in whoever's head started the run.
      */}
      {goldenCaseId !== null && onOpenCase !== undefined && (
        <Button
          variant="tertiary"
          tone="brand"
          size="small"
          title="Ver o caso do conjunto de referência que originou esta simulação"
          onClick={() => onOpenCase(goldenCaseId)}
        >
          Gabarito: {goldenCaseId}
        </Button>
      )}
    </>
  )

  if (quotation === undefined) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4 p-5 pt-0">
        <div className="flex flex-wrap items-center gap-3">
          {back}
          <h1 className="text-tH4 text-text-strong">Simulação</h1>
        </div>

        {/* Same two columns as the editor, and in the same order: the
            prescription holds its place on the left while the progress sits
            in the slot the form will take, so the reading finishing swaps one
            for the other instead of rearranging the page. */}
        <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto lg:grid-cols-2 lg:grid-rows-[minmax(0,1fr)] lg:overflow-visible">
          {imageUrl !== undefined && (
            <img
              src={imageUrl}
              alt="Receita enviada"
              className="border-stroke-weak max-h-[70vh] self-start rounded-[8px] border object-contain lg:h-full lg:max-h-full lg:self-stretch"
            />
          )}
          <Preparing status={status} failed={status === 'failed'} />
        </div>

        {error !== undefined && <p className="text-tP text-text-error">{error}</p>}
      </div>
    )
  }

  // The document is step 0; each preparation is a step after it. Dispatch
  // belongs to the last one, so the decision to run sits at the end of the
  // sequence rather than following you down the page.
  // The document, then one per preparation, then the rule scenario.
  const lastStep = quotation.preparations.length + 1
  const onLastStep = step >= lastStep

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        {back}
        <h1 className="text-tH4 text-text-strong">Orçamento simulado</h1>
      </div>

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}

      {/*
        Two columns that fill the window, with only the form scrolling.
        Below `lg` they stack, and pinning a footer inside a stacked column
        would leave the form a few centimetres tall -- so there the grid itself
        scrolls instead. Either way the window never does.
      */}
      <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto lg:grid-cols-2 lg:grid-rows-[minmax(0,1fr)] lg:overflow-visible">
        {imageUrl !== undefined && (
          <img
            src={imageUrl}
            alt="Receita enviada"
            className="border-stroke-weak max-h-[75vh] self-start rounded-[8px] border object-contain lg:max-h-full lg:h-full lg:self-stretch"
          />
        )}

        <div className="rounded-base border-stroke-weak flex min-h-0 flex-col overflow-hidden border">
          <ProgressTrack
            steps={[
              { id: 'doc', title: 'Paciente e prescritor', tone: 'clean' as const },
              ...quotation.preparations.map((preparation, i) => ({
                id: `f${i}`,
                title: preparation.form ?? `Preparação ${i + 1}`,
                // Every step neutral. The rail wears red in the conference
                // because a step there *concluded* something and red means a
                // discrepancy a pharmacist has to answer. Nothing is being
                // compared here -- this is a form being filled in -- so
                // colouring a step red said a quotation somebody is halfway
                // through typing is wrong.
                tone: 'clean' as const,
              })),
              /*
                The scenario last, because it is not part of the quotation.
                
                Everything before it is what a pharmacist keyed; this is what
                the ERP would have said around it -- the prescription's date,
                the customer's preferences, which branch compounds it. None of
                that can be read off a page, so without this step every
                operational rule is unreachable in a simulation.
              */
              { id: 'rules', title: 'Cenário das regras', tone: 'clean' as const },
            ]}
            index={step}
            visited={visited}
            onJump={goToStep}
            counterText={`Passo ${step + 1} de ${lastStep + 1}`}
            ariaLabel="Partes do orçamento simulado"
          />

          {/* The only part that scrolls. The rail above and the buttons below
              stay put, so navigation is never something you scroll to find. */}
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
            {step === 0 ? (
              /* The same card the preparations get. It is one step among
                 several, and rendering it as bare fields made the first thing
                 anybody saw look like a different screen. */
              <article className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-3 border p-4">
                <h3 className="text-tPSemi text-text-strong">Paciente e prescritor</h3>
                <Input
                  label="Paciente"
                  value={quotation.patientName ?? ''}
                  onChange={(e) => editDocument({ patientName: e.target.value })}
                />
                <Input
                  label="Prescritor"
                  value={quotation.prescriberName ?? ''}
                  onChange={(e) => editDocument({ prescriberName: e.target.value })}
                />
                <Input
                  label="Registro profissional"
                  helperText="CRM, CRMV, CRN… como estaria no sistema. Vazio se não houver."
                  value={quotation.prescriberRegistration ?? ''}
                  onChange={(e) => editDocument({ prescriberRegistration: e.target.value })}
                />
                {quotation.preparations.length === 0 && (
                  <p className="text-tP text-text-weak">
                    Nenhuma preparação foi lida. Dispare assim para ver o que a conferência diz
                    sobre um orçamento vazio.
                  </p>
                )}
              </article>
            ) : step === lastStep ? (
              <RuleScenario
                scenario={quotation.rules ?? {}}
                onChange={(patch) =>
                  setQuotation((c) =>
                    c === undefined ? c : { ...c, rules: { ...(c.rules ?? {}), ...patch } },
                  )
                }
              />
            ) : (
              <PreparationEditor
                preparations={quotation.preparations}
                only={step - 1}
                /*
                  Adding a preparation belongs to the form, not to the bar
                  under it. It was beside the step arrows, where it read as a
                  way to move rather than a way to change what is here -- and
                  the golden-set screen already puts it inside.
                */
                onAddPreparation={() => {
                  setQuotation((c) =>
                    c === undefined
                      ? c
                      : {
                          ...c,
                          preparations: [
                            ...c.preparations,
                            { form: null, posology: null, quantity: null, ingredients: [] },
                          ],
                        },
                  )
                  // Land on what was just added rather than leaving it behind
                  // a step nobody navigated to.
                  goToStep(quotation.preparations.length + 1)
                }}
                onPreparationChange={(f, patch) => mapPreparation(f, (x) => ({ ...x, ...patch }))}
                onIngredientChange={(f, c, patch) =>
                  mapPreparation(f, (x) => ({
                    ...x,
                    ingredients: x.ingredients.map((y, i) => (i === c ? { ...y, ...patch } : y)),
                  }))
                }
                onRemovePreparation={(f) => {
                  setQuotation((c) =>
                    c === undefined
                      ? c
                      : { ...c, preparations: c.preparations.filter((_, i) => i !== f) },
                  )
                  goToStep(Math.min(step, quotation.preparations.length - 1))
                }}
                onAddIngredient={(f) =>
                  mapPreparation(f, (x) => ({
                    ...x,
                    // A blank code, deliberately: a resolved one would mean
                    // this quotation had already survived catalogue
                    // resolution, which is one of the things a simulation
                    // exists to exercise.
                    ingredients: [...x.ingredients, { name: '', dose: null, erpCode: '' }],
                  }))
                }
                onRemoveIngredient={(f, c) =>
                  mapPreparation(f, (x) => ({
                    ...x,
                    ingredients: x.ingredients.filter((_, i) => i !== c),
                  }))
                }
                renderIngredientExtra={(f, c) => {
                  const ingredient = quotation.preparations[f]?.ingredients[c]
                  if (ingredient === undefined) return null
                  return (
                    <ProductPicker
                      label={`Produto do ativo ${c + 1} da preparação ${f + 1}`}
                      suggestion={ingredient.name}
                      value={ingredient.erpCode}
                      onPick={({ erpCode, productName }) =>
                        mapPreparation(f, (x) => ({
                          ...x,
                          ingredients: x.ingredients.map((y, i) =>
                            i === c
                              ? {
                                  ...y,
                                  erpCode,
                                  // The name follows the product, because on
                                  // the quoted side they are the same thing.
                                  // Clearing the product leaves the typed name
                                  // alone: an unresolved line still has a name.
                                  name: productName === '' ? y.name : productName,
                                }
                              : y,
                          ),
                        }))
                      }
                    />
                  )
                }}
              />
            )}
          </div>

          <div className="border-stroke-weak bg-background-sunken flex shrink-0 flex-wrap items-center gap-2 border-t p-4">
            {/* The same arrow pair as the golden-set screen: same gesture,
                same shape, and the bar no longer changes width as the last
                step hides a button. */}
            <Button
              variant="secondary"
              tone="neutral"
              size="small"
              leadingIcon={icons.back}
              aria-label="Etapa anterior"
              title="Etapa anterior"
              disabled={step === 0}
              onClick={() => goToStep(step - 1)}
            />
            <Button
              variant="secondary"
              tone="neutral"
              size="small"
              leadingIcon={icons.forward}
              aria-label="Próxima etapa"
              title="Próxima etapa"
              disabled={onLastStep}
              onClick={() => goToStep(step + 1)}
            />

            <span className="grow" />

            {/* Only at the end. Dispatching is the one irreversible thing on
                this page, and a button that follows you down every step is one
                you eventually press by accident. */}
            {onLastStep && (
              <Button
                variant="primary"
                tone="brand"
                size="small"
                disabled={busy}
                onClick={dispatch}
              >
                {busy ? 'Disparando…' : 'Disparar conferência'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
